import { useState, useMemo } from "react";
import { fmtLongDate, ordenarPlatosDeMenu } from "../../domain/eventoDetalleUtils";
import { IoPrintOutline } from "react-icons/io5";
import styles from "../../styles/evento-detalle.module.css";

const getVinoLabel = (v) => {
    if (!v) return "—";
    const nombre = v.nombre || `Vino #${v.id ?? "—"}`;
    const tipo = v.tipo ? ` • ${v.tipo}` : "";
    return `${nombre}${tipo}`;
};

export function TabMenus({ evento, vinos }) {
    const [openMenus, setOpenMenus] = useState(new Set());

    const vinosIndex = useMemo(() => {
        const arr = Array.isArray(vinos) ? vinos : [];
        return new Map(arr.map((v) => [Number(v.id), v]));
    }, [vinos]);

    const menus = useMemo(() => {
        const raw = Array.isArray(evento?.menus) ? evento.menus : [];

        return raw.map((m) => {
            // 1) Platos ordenados
            const platos = ordenarPlatosDeMenu(m);

            // 2) Normalizar vinos del menú
            // Caso A: vienen objetos
            const vinosObj = Array.isArray(m?.vinos) ? m.vinos : [];

            // Caso B: vienen ids
            const ids =
                Array.isArray(m?.vinoIds) ? m.vinoIds :
                    Array.isArray(m?.vinosIds) ? m.vinosIds :
                        [];

            // Caso C: por etapas (si existiera)
            // { ap:[id], pr:[id], po:[id] } o { ap:[obj], ... }
            const porEtapa = m?.vinosPorEtapa || null;

            // Construimos lista final (con etapa opcional)
            let vinosFinal = [];

            if (porEtapa && typeof porEtapa === "object") {
                const stages = [
                    ["ap", "Aperitivo"],
                    ["pr", "Primer plato"],
                    ["po", "Postre"],
                ];

                vinosFinal = stages.flatMap(([key, label]) => {
                    const list = Array.isArray(porEtapa[key]) ? porEtapa[key] : [];
                    return list.map((x) => {
                        const id = typeof x === "object" ? x?.id : x;
                        const obj = typeof x === "object" ? x : vinosIndex.get(Number(id));
                        return { id: Number(id), etapa: label, vino: obj || null };
                    });
                });
            } else if (vinosObj.length) {
                vinosFinal = vinosObj.map((v) => ({ id: Number(v.id), etapa: null, vino: v }));
            } else if (ids.length) {
                vinosFinal = ids.map((id) => ({
                    id: Number(id),
                    etapa: null,
                    vino: vinosIndex.get(Number(id)) || null,
                }));
            } else {
                vinosFinal = [];
            }

            // Orden: primero por etapa si existe, luego por nombre
            const stageOrder = new Map([
                ["Aperitivo", 0],
                ["Primer plato", 1],
                ["Postre", 2],
            ]);

            vinosFinal.sort((a, b) => {
                const sa = a.etapa ? stageOrder.get(a.etapa) ?? 99 : 99;
                const sb = b.etapa ? stageOrder.get(b.etapa) ?? 99 : 99;
                if (sa !== sb) return sa - sb;
                const la = (a.vino?.nombre || String(a.id)).toLowerCase();
                const lb = (b.vino?.nombre || String(b.id)).toLowerCase();
                return la.localeCompare(lb, "es");
            });

            return { ...m, platos, vinosFinal };
        });
    }, [evento, vinosIndex]);

    const toggleMenu = (idx) => {
        setOpenMenus((prev) => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    const isMenuOpen = (idx) => openMenus.has(idx);

    if (!menus.length) {
        return <div className={styles.hint}>Este evento no tiene menús.</div>;
    }

    return (
        <div className={styles.stack}>
            {menus.map((m, idx) => {
                const open = isMenuOpen(idx);

                const resumenMenu = [
                    fmtLongDate(m.fecha || evento.fechaInicio),
                    (m.comensales ?? evento.numeroComensales) != null
                        ? `${m.comensales ?? evento.numeroComensales} comensales`
                        : null,
                    m.lugar || evento.lugar || evento.ubicacion || null,
                ]
                    .filter(Boolean)
                    .join(" • ");

                const platos = Array.isArray(m.platos) ? m.platos : [];
                const vinosFinal = Array.isArray(m.vinosFinal) ? m.vinosFinal : [];

                return (
                    <div
                        key={m.id ?? `menu-${idx}`}
                        className={`${styles["item-card"]} ${styles.accordion} ${open ? styles.open : ""}`}
                    >
                        <button
                            type="button"
                            className={styles["accordion-header"]}
                            onClick={() => toggleMenu(idx)}
                            aria-expanded={open}
                            aria-controls={`menu-body-${idx}`}
                        >
                            <div className={styles["accordion-title"]}>
                                <strong>{m.nombre || `Menú ${idx + 1}`}</strong>
                                <span className={styles.muted}>— {resumenMenu}</span>
                            </div>

                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.ghost}`}
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        if (!m.id) {
                                            alert("Este menú no tiene id. No se puede imprimir.");
                                            return;
                                        }

                                        const lang = m._lang || "es";
                                        const url = `/print/menu?eventId=${evento.id}&menuId=${m.id}&lang=${lang}`;
                                        window.open(url, "_blank", "noopener,noreferrer");
                                    }}
                                    title="Imprimir menú"
                                >
                                    <IoPrintOutline style={{ fontSize: "15px" }} /> Imprimir
                                </button>

                                <span className={styles.chevron} aria-hidden>▾</span>
                            </div>
                        </button>

                        <div id={`menu-body-${idx}`} className={styles["accordion-body"]}>
                            {/* PLATOS */}
                            <div style={{ marginBottom: 12 }}>
                                <strong>Platos</strong>
                                {platos.length === 0 ? (
                                    <div className={styles.hint}>Menú sin platos.</div>
                                ) : (
                                    <ul className={styles.chips}>
                                        {platos.map((plato) => (
                                            <li key={plato.id ?? plato.nombre} className={styles.chip}>
                                                {plato.nombre || `Plato ${plato.id}`}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* VINOS */}
                            <div>
                                <strong>Vinos</strong>
                                {vinosFinal.length === 0 ? (
                                    <div className={styles.hint}>Menú sin vinos.</div>
                                ) : (
                                    <ul className={styles.chips}>
                                        {vinosFinal.map((row) => (
                                            <li key={`${row.id}-${row.etapa || "x"}`} className={styles.chip}>
                                                {row.etapa ? <strong>{row.etapa}:</strong> : null}{" "}
                                                {row.vino ? getVinoLabel(row.vino) : `Vino #${row.id}`}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
